/**
 * Every sound the product makes, and the rules about when.
 *
 * Originally the check-in alone. The three alert cards were deliberately
 * silent — ignoring a check-in costs an availability score and ignoring a
 * resume chase costs nothing — and that was reversed on purpose: they now
 * sound too, tiered so the ear can tell them apart without looking.
 *
 * The tiers escalate by shape rather than by volume. A louder version of the
 * same noise tells you something happened; a different shape tells you what.
 * One soft low note for a resume chase, two rising for a joining check-in,
 * three rising and brighter for an interview — and the check-in keeps its own
 * two-tone signature so it is never mistaken for any of them.
 *
 * Kept out of the components so the rules live in one place rather than in a
 * useEffect that also does four other things.
 */

/** How loud a thing is, in the only sense that matters here: what it costs. */
export type AlertTier = 'moderate' | 'high' | 'urgent';

const SOUND_KEY = 'ats.checkInSound';

/** Whether the beep is on. Absent means on: a check-in you miss costs you. */
export function soundEnabled(): boolean {
  return localStorage.getItem(SOUND_KEY) !== 'off';
}

export function setSoundEnabled(on: boolean): void {
  localStorage.setItem(SOUND_KEY, on ? 'on' : 'off');
}

//: Each tier's notes, in hertz, played in order.
//:
//: Chosen so no two share a first note — the difference is audible in the
//: first fifth of a second, before the pattern has finished.
const TIERS: Record<AlertTier, number[]> = {
  // A resume that has not arrived. Low, single, easy to ignore on purpose.
  moderate: [520],
  // Somebody who joined and is due a call. Two rising, warmer than the
  // check-in so the two do not blur.
  high: [660, 784],
  // An interview, which has a time and stops mattering once it passes.
  urgent: [988, 1319, 1568],
};

/**
 * Play one tier's tone. Silent when the person has muted, same as the
 * check-in — one preference covers all four, so silencing the noisy ones
 * silences the important one too. Worth knowing before turning it off.
 */
export function alertTone(tier: AlertTier): void {
  play(TIERS[tier]);
}

/**
 * A short two-tone beep, synthesised rather than bundled.
 *
 * No asset to ship, nothing for a strict CSP to refuse, and no network fetch
 * that could be slower than the thing it is announcing.
 *
 * Browsers block audio until the page has been interacted with. That is
 * satisfied here by having logged in, but a hard refresh can still swallow the
 * first one — which is exactly why this is never the only signal.
 */
export function beep(): void {
  play([880, 1174]);
}

/**
 * Peak gain, well above the 0.18 this started at — the old level was audible
 * in a quiet room and lost in an office, which is the room that matters.
 *
 * Not 1.0. A sine at full gain clips against anything else the device is
 * playing and comes out as a rasp rather than a note, so this is loud with
 * headroom left. The note also rings a little longer, which carries further
 * than raising the gain again would.
 *
 * The mute switch is unchanged and still covers all four sounds.
 */
const PEAK = 0.6;

/** The synthesiser both the check-in and the alert tiers go through. */
function play(notes: number[]): void {
  if (!soundEnabled()) return;
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) return;

    const ctx = new Ctx();
    const note = (at: number, hz: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = hz;
      // Eased rather than square, because an abrupt stop clicks.
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      gain.gain.exponentialRampToValueAtTime(PEAK, ctx.currentTime + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.26);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + at + 0.2);
    };
    notes.forEach((hz, i) => note(i * 0.16, hz));
    // Let it finish, then release the device rather than holding it open.
    window.setTimeout(() => void ctx.close(), 400 + notes.length * 200);
  } catch {
    // A blocked or unavailable audio device is not worth an error to the
    // person trying to answer a check-in.
  }
}

/** Whether the browser will ever show us a notification again. */
export function notificationState(): NotificationPermission | 'unsupported' {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Ask once. Must be called from a click — browsers ignore or penalise a
 * request made on page load.
 *
 * "denied" is permanent from here: the browser will not prompt again, and only
 * the person can reverse it in site settings. So the caller has to say that
 * rather than offering the button a second time.
 */
export async function requestNotifications(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied';
  return Notification.requestPermission();
}

/**
 * Show the desktop notification, if it would actually add anything.
 *
 * Only when the tab is hidden. Somebody looking at the modal does not need the
 * operating system to tell them it is there, and a notification they can see
 * behind the thing it describes is noise.
 *
 * Tagged, so a second call replaces the first rather than stacking a queue of
 * identical prompts while somebody is at lunch.
 */
export function notifyCheckIn(): void {
  if (notificationState() !== 'granted') return;
  if (document.visibilityState !== 'hidden') return;
  try {
    const note = new Notification('Check-in required', {
      body: 'Your shift check-in is waiting. Unanswered ones count as missed.',
      tag: 'ats-check-in',
      requireInteraction: false,
    });
    note.onclick = () => {
      window.focus();
      note.close();
    };
  } catch {
    // Some browsers throw on construction rather than returning a rejection.
  }
}
